import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { MessageSquare } from 'lucide-react';

import { API_URL } from '../../utils/api';
import { getErrorMessage } from '../../utils/errors';

const MODES = [
  { value: 'off', label: 'Off: pay at the end, like before' },
  { value: 'optional', label: 'Optional: offer to pay before the invitation is made' },
  { value: 'required', label: 'Required: everyone pays before the invitation is made' },
];

/** SMS bot settings: daily message limits per phone and pay-first (backend /admin/sms-settings). */
export function SmsSettingsCard() {
  const [cfg, setCfg] = useState(null);
  const [phones, setPhones] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/admin/sms-settings`)
      .then((r) => {
        setCfg(r.data);
        setPhones((r.data.pay_first_test_phones || []).join('\n'));
      })
      .catch((e) => console.error('Failed to fetch SMS settings:', e));
  }, []);

  if (!cfg) return null;

  const num = (key) => (e) => setCfg({ ...cfg, [key]: e.target.value === '' ? '' : parseInt(e.target.value, 10) });

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API_URL}/admin/sms-settings`, {
        text_daily_limit: cfg.text_daily_limit,
        mms_daily_limit: cfg.mms_daily_limit,
        agent_daily_limit: cfg.agent_daily_limit,
        pay_first_deposit: cfg.pay_first_deposit,
        deposit_preview_limit: cfg.deposit_preview_limit,
        pay_first_mode: cfg.pay_first_mode,
        pay_first_test_phones: phones,
      });
      setCfg(r.data);
      setPhones((r.data.pay_first_test_phones || []).join('\n'));
      toast.success('SMS settings saved');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save SMS settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-gold" />
          SMS Bot
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label htmlFor="sms_text_limit">Texts per day</Label>
            <Input id="sms_text_limit" type="number" min="0" value={cfg.text_daily_limit} onChange={num('text_daily_limit')} className="mt-2" />
          </div>
          <div>
            <Label htmlFor="sms_mms_limit">Pictures per day</Label>
            <Input id="sms_mms_limit" type="number" min="0" value={cfg.mms_daily_limit} onChange={num('mms_daily_limit')} className="mt-2" />
          </div>
          <div>
            <Label htmlFor="sms_agent_limit">AI changes per day</Label>
            <Input id="sms_agent_limit" type="number" min="0" value={cfg.agent_daily_limit} onChange={num('agent_daily_limit')} className="mt-2" />
          </div>
        </div>
        <p className="text-sm text-ink/50 -mt-4">
          Per phone number, per day (New York time). 0 = no limit. Admins are not limited. Over the picture limit the
          preview goes out as text; over the text limit the customer gets one &quot;try again tomorrow&quot; text.
          Paid invitations are always delivered.
        </p>

        <div>
          <Label htmlFor="sms_pay_first">Pay first</Label>
          <select
            id="sms_pay_first"
            value={cfg.pay_first_mode}
            onChange={(e) => setCfg({ ...cfg, pay_first_mode: e.target.value })}
            className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <p className="text-sm text-ink/50 mt-1">
            Customers are told: &quot;To prevent abuse of the system, we charge the deposit now. It is credited to
            your invitation.&quot; The deposit unlocks the AI designer (reply AGENT) and the picture previews below;
            at checkout only the balance is charged. Without it, customers see text previews only.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="sms_deposit">Deposit ($)</Label>
            <Input id="sms_deposit" type="number" step="0.01" min="1" value={cfg.pay_first_deposit}
              onChange={(e) => setCfg({ ...cfg, pay_first_deposit: e.target.value === '' ? '' : parseFloat(e.target.value) })} className="mt-2" />
          </div>
          <div>
            <Label htmlFor="sms_deposit_previews">Picture previews with deposit</Label>
            <Input id="sms_deposit_previews" type="number" min="0" value={cfg.deposit_preview_limit} onChange={num('deposit_preview_limit')} className="mt-2" />
          </div>
        </div>

        <div className={`rounded-md p-3 text-sm ${cfg.pay_first_live ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-900'}`}>
          {cfg.pay_first_live
            ? 'Pay-first charges are LIVE: cards are charged for real.'
            : 'Pay-first charges are in TEST MODE: no card is charged, and only admins and the test phones below are offered pay-first. Going live is a server setting (SMS_PAY_FIRST_LIVE).'}
        </div>

        <div>
          <Label htmlFor="sms_test_phones">Test phones (one per line)</Label>
          <Textarea id="sms_test_phones" rows={3} value={phones} onChange={(e) => setPhones(e.target.value)} className="mt-2" placeholder="845-555-0101" />
        </div>

        <Button onClick={save} disabled={saving} className="w-full bg-ink text-paper hover:bg-ink/90">
          {saving ? 'Saving...' : 'Save SMS Settings'}
        </Button>
      </CardContent>
    </Card>
  );
}
